import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Typography,
  Box,
  Divider,
  Chip,
} from '@mui/material';
import { Close as CloseIcon, DragIndicator } from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  restrictToVerticalAxis,
  restrictToParentElement,
} from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { updateItem } from '../../redux/actions/posMenu';
import { getTranslationValue } from '../../constants/posMenu';

const SortableModifierGroup = ({ modifierGroup, menuMap }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: modifierGroup._id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.8 : 1,
  };

  const optionsCount = Array.isArray(modifierGroup.optionIds)
    ? modifierGroup.optionIds.length
    : 0;

  return (
    <ListItem
      ref={setNodeRef}
      style={style}
      sx={{
        mb: 1,
        backgroundColor: isDragging ? '#e3f2fd' : '#f5f5f5',
        borderRadius: 1,
        border: '1px solid #e0e0e0',
        '&:hover': {
          backgroundColor: '#eeeeee',
        },
        cursor: isDragging ? 'grabbing' : 'grab',
      }}
      {...attributes}
      {...listeners}
    >
      <ListItemIcon>
        <DragIndicator sx={{ color: '#9E9EAD' }} />
      </ListItemIcon>
      <ListItemText
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {getTranslationValue(modifierGroup.name)}
            </Typography>
            {optionsCount > 0 && (
              <Chip
                label={`${optionsCount} option(s)`}
                size="small"
                color="primary"
              />
            )}
          </Box>
        }
        secondary={
          <Typography variant="body2" color="text.secondary">
            Min: {modifierGroup.min ?? '-'} | Max: {modifierGroup.max ?? '-'} |
            Free: {modifierGroup.free ?? '-'}
          </Typography>
        }
      />
    </ListItem>
  );
};

const ModifierGroupSortModal = ({ open, onClose, item, menuMap }) => {
  const dispatch = useDispatch();
  const [sortedModifierGroups, setSortedModifierGroups] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Initialize sorted modifier groups when modal opens
  useEffect(() => {
    if (open && item?.modifierGroupIds) {
      const modifierGroups = (item.modifierGroupIds || [])
        .map((mgId) => menuMap?.modifierGroups?.[mgId])
        .filter(Boolean);
      setSortedModifierGroups(modifierGroups);
      setHasChanges(false);
    }
  }, [open, item?.modifierGroupIds, menuMap]);

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setSortedModifierGroups((items) => {
        const oldIndex = items.findIndex((item) => item._id === active.id);
        const newIndex = items.findIndex((item) => item._id === over.id);

        const newOrder = arrayMove(items, oldIndex, newIndex);
        setHasChanges(true);
        return newOrder;
      });
    }
  }, []);

  const handleSave = async () => {
    if (hasChanges && item) {
      setIsSaving(true);
      const modifierGroupIds = sortedModifierGroups.map((mg) => mg._id);
      try {
        await dispatch(
          updateItem(item._id, {
            modifierGroupIds,
            provider: 'pos',
          })
        );
        toast.success('Modifier group order saved');
        setHasChanges(false);
        onClose();
      } catch (error) {
        toast.error(error.message || 'Failed to save order');
      } finally {
        setIsSaving(false);
      }
    } else {
      onClose();
    }
  };

  const handleCancel = () => {
    if (item?.modifierGroupIds) {
      const modifierGroups = (item.modifierGroupIds || [])
        .map((mgId) => menuMap?.modifierGroups?.[mgId])
        .filter(Boolean);
      setSortedModifierGroups(modifierGroups);
    }
    setHasChanges(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: '400px',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Box>
          <Typography variant="h6" component="div">
            Sort Modifier Groups
          </Typography>
          <Typography variant="body2" color="text.secondary">
            for {getTranslationValue(item?.name)}
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={handleCancel}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2, overflowX: 'hidden' }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Drag and drop modifier groups to reorder them. Changes will be saved
          when you click &quot;Save&quot;.
        </Typography>

        {sortedModifierGroups.length > 0 ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            modifiers={[restrictToVerticalAxis, restrictToParentElement]}
          >
            <SortableContext
              items={sortedModifierGroups.map((mg) => mg._id)}
              strategy={verticalListSortingStrategy}
            >
              <List sx={{ padding: 0, overflowX: 'hidden' }}>
                {sortedModifierGroups.map((modifierGroup) => (
                  <SortableModifierGroup
                    key={modifierGroup._id}
                    modifierGroup={modifierGroup}
                    menuMap={menuMap}
                  />
                ))}
              </List>
            </SortableContext>
          </DndContext>
        ) : (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '200px',
            }}
          >
            <Typography variant="body1" color="text.secondary">
              No modifier groups to sort
            </Typography>
          </Box>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={handleCancel} variant="outlined" sx={{ mr: 1 }}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!hasChanges || isSaving}
          sx={{
            backgroundColor: hasChanges ? '#1976d2' : undefined,
          }}
        >
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModifierGroupSortModal;

